import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUnits, createBooking, ANREDEN, GRUPPENARTEN, type NewBookingPayload } from "@/api/newBooking";
import { getSettingsConfig } from "@/api/settings";
import type { ApiError } from "@/api/client";
import { Btn } from "@/components/Btn";
import { ConflictModal, type ConflictEntry } from "@/components/dialogs/ConflictModal";
import { useDateRange } from "@/lib/useDateRange";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

type FormState = {
  gruppenname: string;
  anrede: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  einheiten_ids: number[];
  check_in: string;
  check_out: string;
  check_in_time: string;
  check_out_time: string;
  gruppenart: string;
  adresse_strasse: string;
  adresse_plz: string;
  adresse_ort: string;
  adresse_land: string;
  bemerkung: string;
  is_intern: boolean;
  anzahlung_betrag: string;
  nachreinigung_eur: string;
};

const INITIAL: FormState = {
  gruppenname: "",
  anrede: "",
  vorname: "",
  nachname: "",
  email: "",
  telefon: "",
  einheiten_ids: [],
  check_in: "",
  check_out: "",
  check_in_time: "15:00",
  check_out_time: "10:00",
  gruppenart: "",
  adresse_strasse: "",
  adresse_plz: "",
  adresse_ort: "",
  adresse_land: "DE",
  bemerkung: "",
  is_intern: false,
  anzahlung_betrag: "",
  nachreinigung_eur: "0",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--fg-muted)",
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: ".04em",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid var(--border)",
  borderRadius: 6,
  background: "var(--bg-card)",
  color: "var(--fg)",
  fontFamily: "var(--font-body)",
  boxSizing: "border-box",
};

const fieldStyle: React.CSSProperties = { marginBottom: 16 };

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: "var(--accent)", marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Input({
  value,
  onChange,
  type = "text",
  placeholder,
  min,
  max,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  min?: string;
  max?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    />
  );
}

function SelectField({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[] | { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {(options as (string | { value: string; label: string })[]).map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return (
          <option key={v} value={v}>
            {l}
          </option>
        );
      })}
    </select>
  );
}

export function NewBookingPage() {
  useDocumentTitle("Neue Buchung");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<ConflictEntry[] | null>(null);
  const [pendingPayload, setPendingPayload] = useState<NewBookingPayload | null>(null);

  const { data: units = [] } = useQuery({
    queryKey: ["units"],
    queryFn: fetchUnits,
  });

  // Anzahlung mit dem Config-Default vorbelegen, solange der Nutzer nichts eingetragen hat.
  const { data: settingsConfig } = useQuery({
    queryKey: ["settings-config"],
    queryFn: getSettingsConfig,
    retry: false,
  });
  useEffect(() => {
    if (settingsConfig?.ANZAHLUNG_BETRAG != null) {
      setForm((f) =>
        f.anzahlung_betrag === "" ? { ...f, anzahlung_betrag: settingsConfig.ANZAHLUNG_BETRAG } : f,
      );
    }
  }, [settingsConfig]);

  const mutation = useMutation({
    mutationFn: ({ payload, force }: { payload: NewBookingPayload; force: boolean }) =>
      createBooking(payload, force),
    onSuccess: (bk) => {
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      navigate(`/bookings/${bk.id}`);
    },
    onError: (err: unknown) => {
      const apiErr = err as ApiError;
      if (apiErr?.status === 409) {
        const body = apiErr.body as { detail?: { conflicts?: ConflictEntry[] } };
        const cs = body?.detail?.conflicts;
        if (cs && cs.length > 0) {
          setConflicts(cs);
          return;
        }
      }
      setError((err as Error).message || "Fehler beim Anlegen der Buchung.");
    },
  });

  const set = (key: keyof FormState) => (v: string) =>
    setForm((f) => ({ ...f, [key]: v }));

  const dateRange = useDateRange(
    form.check_in,
    form.check_out,
    ({ from, to }) => setForm((f) => ({ ...f, check_in: from, check_out: to })),
    { minNights: 1, keepDuration: true },
  );

  function toggleUnit(id: number) {
    setForm((f) => {
      const ids = f.einheiten_ids.includes(id)
        ? f.einheiten_ids.filter((x) => x !== id)
        : [...f.einheiten_ids, id];
      return { ...f, einheiten_ids: ids };
    });
  }

  /** "120,50" → 120.5; leer → undefined; ungültig → NaN. */
  function parseBetrag(s: string): number | undefined {
    const t = s.trim().replace(",", ".");
    if (t === "") return undefined;
    return Number(t);
  }

  function validate(): string | null {
    if (!form.gruppenname.trim()) return "Gruppenname ist erforderlich.";
    if (!form.anrede) return "Bitte eine Anrede wählen.";
    if (!form.vorname.trim()) return "Vorname ist erforderlich.";
    if (!form.nachname.trim()) return "Nachname ist erforderlich.";
    if (!form.email.trim() || !form.email.includes("@"))
      return "Bitte eine gültige E-Mail-Adresse angeben.";
    if (form.einheiten_ids.length === 0) return "Bitte mindestens eine Einheit wählen.";
    if (!form.check_in) return "Anreisedatum ist erforderlich.";
    if (!form.check_out) return "Abreisedatum ist erforderlich.";
    if (form.check_in >= form.check_out) return "Abreise muss nach Anreise liegen.";
    const az = parseBetrag(form.anzahlung_betrag);
    if (az !== undefined && (Number.isNaN(az) || az < 0))
      return "Anzahlung muss eine Zahl ≥ 0 sein.";
    const nr = parseBetrag(form.nachreinigung_eur);
    if (nr !== undefined && (Number.isNaN(nr) || nr < 0))
      return "Nachreinigung muss eine Zahl ≥ 0 sein.";
    return null;
  }

  function buildPayload(): NewBookingPayload {
    return {
      gruppenname: form.gruppenname.trim(),
      anrede: form.anrede,
      vorname: form.vorname.trim(),
      nachname: form.nachname.trim(),
      email: form.email.trim(),
      telefon: form.telefon.trim(),
      einheiten_ids: form.einheiten_ids,
      check_in: form.check_in,
      check_out: form.check_out,
      check_in_time: form.check_in_time || "15:00",
      check_out_time: form.check_out_time || "10:00",
      gruppenart: form.gruppenart,
      adresse_strasse: form.adresse_strasse.trim(),
      adresse_plz: form.adresse_plz.trim(),
      adresse_ort: form.adresse_ort.trim(),
      adresse_land: form.adresse_land || "DE",
      bemerkung: form.bemerkung.trim(),
      is_intern: form.is_intern,
      anzahlung_betrag: parseBetrag(form.anzahlung_betrag),
      nachreinigung_eur: parseBetrag(form.nachreinigung_eur),
    };
  }

  function submit() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    const payload = buildPayload();
    setPendingPayload(payload);
    mutation.mutate({ payload, force: false });
  }

  function forceSubmit() {
    if (!pendingPayload) return;
    setConflicts(null);
    mutation.mutate({ payload: pendingPayload, force: true });
  }

  return (
    <>
      {conflicts && (
        <ConflictModal
          conflicts={conflicts}
          headline={
            <>
              Im gewählten Zeitraum gibt es bereits{" "}
              <strong>
                {conflicts.length} kollidierende Buchung
                {conflicts.length !== 1 ? "en" : ""}
              </strong>
              :
            </>
          }
          cancelLabel="Daten ändern"
          confirmLabel="Trotzdem anlegen"
          onForce={forceSubmit}
          onCancel={() => setConflicts(null)}
        />
      )}

      <div style={{ padding: "24px 32px", maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--fg-muted)",
              padding: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: 22 }}>
              arrow_back
            </span>
          </button>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "var(--fg)", margin: 0 }}>
            Neue Buchung anlegen
          </h1>
        </div>

        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            padding: 24,
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 20px", color: "var(--fg)" }}>
            Kontaktdaten
          </h2>
          <Field label="Gruppenname" required>
            <Input
              value={form.gruppenname}
              onChange={set("gruppenname")}
              placeholder="z. B. Stamm Waldläufer"
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Anrede" required>
              <SelectField
                value={form.anrede}
                onChange={set("anrede")}
                options={ANREDEN}
                placeholder="Bitte wählen"
              />
            </Field>
            <Field label="Gruppenart">
              <SelectField
                value={form.gruppenart}
                onChange={set("gruppenart")}
                options={GRUPPENARTEN}
                placeholder="Bitte wählen"
              />
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Vorname" required>
              <Input value={form.vorname} onChange={set("vorname")} />
            </Field>
            <Field label="Nachname" required>
              <Input value={form.nachname} onChange={set("nachname")} />
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="E-Mail" required>
              <Input value={form.email} onChange={set("email")} type="email" />
            </Field>
            <Field label="Telefon">
              <Input value={form.telefon} onChange={set("telefon")} type="tel" />
            </Field>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "20px 0" }} />

          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 20px", color: "var(--fg)" }}>
            Aufenthalt
          </h2>

          <Field label="Einheiten" required>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {units.map((u) => {
                const selected = form.einheiten_ids.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleUnit(u.id)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 6,
                      border: `2px solid ${selected ? "var(--brand-700)" : "var(--border)"}`,
                      background: selected
                        ? "color-mix(in oklab, var(--brand-700) 12%, transparent)"
                        : "var(--bg-card)",
                      color: selected ? "var(--brand-700)" : "var(--fg)",
                      fontWeight: selected ? 700 : 400,
                      cursor: "pointer",
                      fontSize: 14,
                      transition: "all .12s",
                    }}
                  >
                    {u.name}
                  </button>
                );
              })}
            </div>
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <Field label="Anreise" required>
              <Input value={form.check_in} onChange={dateRange.setFrom} type="date" max={dateRange.fromProps.max} />
            </Field>
            <Field label="Anreisezeit">
              <Input value={form.check_in_time} onChange={set("check_in_time")} type="time" />
            </Field>
            <Field label="Abreise" required>
              <Input value={form.check_out} onChange={dateRange.setTo} type="date" min={dateRange.toProps.min} />
            </Field>
            <Field label="Abreisezeit">
              <Input value={form.check_out_time} onChange={set("check_out_time")} type="time" />
            </Field>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "20px 0" }} />

          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 20px", color: "var(--fg)" }}>
            Beträge
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Anzahlung (€)">
              <Input
                value={form.anzahlung_betrag}
                onChange={set("anzahlung_betrag")}
                placeholder={settingsConfig?.ANZAHLUNG_BETRAG ?? "100"}
              />
            </Field>
            <Field label="Nachreinigung (€)">
              <Input value={form.nachreinigung_eur} onChange={set("nachreinigung_eur")} placeholder="0" />
            </Field>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "20px 0" }} />

          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 20px", color: "var(--fg)" }}>
            Adresse
          </h2>

          <Field label="Straße">
            <Input value={form.adresse_strasse} onChange={set("adresse_strasse")} placeholder="Musterstraße 1" />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 12 }}>
            <Field label="PLZ">
              <Input value={form.adresse_plz} onChange={set("adresse_plz")} placeholder="12345" />
            </Field>
            <Field label="Ort">
              <Input value={form.adresse_ort} onChange={set("adresse_ort")} placeholder="Musterstadt" />
            </Field>
            <Field label="Land">
              <Input value={form.adresse_land} onChange={set("adresse_land")} placeholder="DE" />
            </Field>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "20px 0" }} />

          <Field label="Notizen">
            <textarea
              value={form.bemerkung}
              onChange={(e) => set("bemerkung")(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical" }}
              placeholder="Interne Hinweise, Sonderwünsche, …"
            />
          </Field>

          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", marginTop: 4 }}>
            <input
              type="checkbox"
              checked={form.is_intern}
              onChange={(e) => setForm((f) => ({ ...f, is_intern: e.target.checked }))}
              style={{ width: 16, height: 16, accentColor: "#607D8B", cursor: "pointer" }}
            />
            <span style={{ fontSize: 13, color: "var(--fg)" }}>
              Interne Buchung (Platzhalter — wird nicht abgerechnet)
            </span>
          </label>

          {error && (
            <div
              style={{
                background: "var(--error-bg, #fef2f2)",
                border: "1px solid var(--error-border, #fca5a5)",
                color: "var(--error-fg, #991b1b)",
                borderRadius: 6,
                padding: "10px 14px",
                fontSize: 13,
                marginTop: 4,
                marginBottom: 8,
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{ display: "flex", justifyContent: "flex-end", marginTop: 24, gap: 10 }}
          >
            <Btn variant="ghost" onClick={() => navigate(-1)}>
              Abbrechen
            </Btn>
            <Btn variant="primary" onClick={submit} disabled={mutation.isPending}>
              {mutation.isPending ? "Wird angelegt…" : "Buchung anlegen"}
            </Btn>
          </div>
        </div>
      </div>
    </>
  );
}
